import React from 'react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CampaignIcon from '@mui/icons-material/Campaign';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolIcon from '@mui/icons-material/School';
import SecurityIcon from '@mui/icons-material/Security';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import DemoViewSwitch from '@/components/common/DemoViewSwitch';
import { ROLES } from '@/config/roles.js';
import { useAuth } from '@/hooks/useAuth';

const UserInfoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const formatRoleLabel = (roleValue: string | undefined): string => {
  if (!roleValue) return 'Student';
  if (roleValue === ROLES.USER) return 'Student';
  // Convert snake_case to Title Case with spaces
  const withSpaces = roleValue.replace(/_/g, ' ');
  return withSpaces
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const getRoleChipProps = (roleValue: string | undefined) => {
  switch (roleValue) {
    case ROLES.SUPER_ADMIN:
      return { color: 'secondary' as const, icon: <SecurityIcon /> };
    case ROLES.ADMIN:
      return { color: 'primary' as const, icon: <AdminPanelSettingsIcon /> };
    case ROLES.TEACHER:
      return { color: 'success' as const, icon: <SchoolIcon /> };
    case ROLES.AFFILIATE:
      return { color: 'warning' as const, icon: <CampaignIcon /> };
    case ROLES.USER:
    default:
      return { color: 'info' as const, icon: <PersonOutlineIcon /> };
  }
};

interface UserInfoProps {
  showDivider?: boolean;
}

const UserInfo = ({ showDivider = true }: UserInfoProps) => {
  const { user, isAuthenticated, userRole } = useAuth();

  if (!isAuthenticated || !user) {
    return null;
  }

  const getUserName = (): string => {
    return (
      (user.displayName as string) ||
      (user.given_name as string) ||
      (user.name as string | undefined)?.split(' ')[0] ||
      'User'
    );
  };

  return (
    <>
      <UserInfoContainer>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            textAlign: 'center',
          }}
        >
          {getUserName()}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            mt: 0.5,
            fontSize: '0.875rem',
          }}
        >
          {user.email}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.25 }}>
          <Chip
            size="small"
            variant={userRole === ROLES.AFFILIATE ? 'filled' : 'outlined'}
            label={formatRoleLabel(userRole)}
            {...getRoleChipProps(userRole)}
            sx={{
              fontWeight: 600,
              letterSpacing: 0.2,
              px: 0.5,
              '& .MuiChip-icon': { fontSize: 18 },
            }}
          />
        </Box>
        <DemoViewSwitch layout="drawer" size="small" />
      </UserInfoContainer>
      {showDivider && <Divider />}
    </>
  );
};

export default UserInfo;
